/**
 * @author NikolaV
 */
import { autorun } from 'mobx';
import { MapOverlays } from '@openlayers/ol-overlays';
import { latLonToEN, setMapCursor } from '@openlayers/ol-utils';
import * as geofences from './geofences/geofence-features-layer';
import { store } from './store/rootStore';
import * as VehicleTripFeatures from './vehicles/vehicles-trips-features';
import { drawTrails } from './vehicles/vehicles-trails';
import { StatsGeofenceMapPopups } from './stats/StatsMapPopups';
import { VehiclesLayer } from '@openlayers/vehicles/VehiclesLayer';
import { VehiclesFeatureFactory } from '@openlayers/vehicles/VehiclesFeatureFactory';
import * as React from 'react';

import Feature from 'ol/Feature';
import { MapWrapper } from '@openlayers/MapWrapper';
import { VehiclesStore } from '@shared/vehicles/VehiclesStore';
import { TileLayerService } from '@openlayers/tile-layers/TileLayerService';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { GeofenceType } from '@shared/trucks/GeofenceFeatureFactory';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { VehicleInfoCard } from '@shared/vehicles/ui/VehicleInfoCard';
import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';


export let mapWrapper: MapWrapper = null;
export let vehiclesLayer: VehiclesLayer = null;

const helsinkiCoord: Coordinate = latLonToEN(60.212808, 24.812256);

export async function init() {
    const tileLayers = await TileLayerService.fetchLayers();
    mapWrapper = new MapWrapper().init('map-wrapper', helsinkiCoord, 16, tileLayers);

    mapWrapper.featureLayers = {
        vehicles: new VehiclesLayer(
            {
                makeFeature: VehiclesFeatureFactory.makeFeature,
                styleFunction: VehiclesFeatureFactory.styleFunction,
            },
            new VehiclesStore()
        ),
    };

    vehiclesLayer = mapWrapper.featureLayers.vehicles;
    mapWrapper.featureLayers.vehicles.addToMap(mapWrapper.getInstance(), 100);

    mapWrapper.backgroundMaps.loadWorksite();
    mapWrapper.events.onClick(onMapClick);
    mapWrapper.events.onMouseMove(onMouseMove);
    mapWrapper.events.onZoom(
        _.throttle(() => {
            const zoom = mapWrapper.getInstance().getView().getZoom() || 12;
            if (zoom < 14.5) {
                geofences.hideLabels();
            } else {
                geofences.showLabels();
            }
        }, 200)
    );

    autorun(() => geofences.makeFeatures(mapWrapper.getInstance()), { delay: 100 });

    autorun(
        () => {
            vehiclesLayer.clear();
            vehiclesLayer.drawAll(store.vehicles.activeProjectVehicles);
        },
        { delay: 100 }
    );
    autorun(drawTrails, { delay: 100 });
    autorun(
        () => {
            if (store.isMapCleared) {
                VehicleTripFeatures.source.clear();
            }
        },
        { delay: 100 }
    );

    store.init();
    setTimeout(loadState, 500);
}

function onMapClick(evt: ClickEventWithCoordinate) {
    const mapInstance = mapWrapper.getInstance();
    const pixel = mapInstance.getEventPixel(evt);
    const [e, n] = evt.coordinate;

    // clear selection, ui elements
    MapOverlays.hideDialog(mapInstance);
    MapOverlays.hideTooltip(mapInstance);
    MapOverlays.hideContextMenu(mapInstance);
    store.geofences.highlightIds = [];
    store.vehicles.highlightIds = [];

    const feature: Feature = mapInstance.forEachFeatureAtPixel(pixel, f => f) as Feature;
    if (!feature) return null;

    const type = feature.get('type');
    const ajaxData = feature.get('data');
    const dialogContent = feature.get('dialogContent');

    if (dialogContent) {
        VehicleTripFeatures.onClick(feature, e, n);
    } else if (type === 'geofence') {
        const data = ajaxData;

        store.activeSidebarTabIndex = 1;

        mapInstance.forEachFeatureAtPixel(pixel, f => {
            store.geofences.highlightIds.push(f.get('data').id);
        });
        setTimeout(() => {
            document.querySelector('#geofences-list .item.selected')?.scrollIntoView();
        });

        if (data.type === GeofenceType.LOAD || data.type === GeofenceType.UNLOAD || data.type === GeofenceType.HYBRID) {
            const dialog = MapOverlays.showDialog(mapInstance, '', e, n, feature);
            ReactUtils.safeRender(
                React.createElement(StatsGeofenceMapPopups, {
                    geofence: data,
                }),
                dialog.getElement()
            );
        }
    } else if (type === 'vehicle') {
        store.activeSidebarTabIndex = 0;

        mapInstance.forEachFeatureAtPixel(pixel, f => {
            store.vehicles.highlightIds.push(f.get('data').id);
        });
        setTimeout(() => {
            document.querySelector('#trucks-vehicles-list .item.selected')?.scrollIntoView();
        });

        WindowManager.open({
            group: 'vehicle-info-card',
            id: ajaxData.id,
            showCloseButton: true,
            allowMultiple: true,
            resize: 'both',
            collapsedTitle: ajaxData.name,
            defaultSizing: {
                width: 430,
                height: 300,
            },
            body: (
                <div style={{ padding: '0 10px 10px', overflow: 'auto' }}>
                    <VehicleInfoCard vehicle={ajaxData} mapWrapper={mapWrapper} isTruckPage={true} />
                </div>
            ),
        });
    }
}

let mouseOverFeature: Feature = null;
const onMouseMove = _.throttle(evt => {
    if (evt.dragging) return;
    const mapInstance = mapWrapper.getInstance();
    if ((evt.originalEvent.target as HTMLElement).nodeName !== 'CANVAS') return;

    const pixel = mapInstance.getEventPixel(evt.originalEvent);
    mapWrapper.setMapCursor('');

    if (!mapWrapper.getInstance().hasFeatureAtPixel(pixel)) {
        clearOverlays();
        return;
    }

    const [e, n] = evt.coordinate;
    const feature = mapInstance.forEachFeatureAtPixel(pixel, feature => feature) as Feature;
    if (MapOverlays.getDialogFeature() == feature) return; // don't show tooltip on opened feature

    const type = feature.get('type');
    const dialogContent = feature.get('dialogContent');

    if (dialogContent) {
        mapWrapper.setMapCursor('pointer');
        return;
    }

    const mouseInteraction: FeatureMouseInteraction = feature.get('mouseInteraction');
    if (mouseInteraction && typeof mouseInteraction.onMouseOver === 'function') {
        if (mouseOverFeature !== feature && !feature.get('no_hover_style') /* perf optim, set() triggers redraw */) {
            if (mouseOverFeature) {
                mouseOverFeature.set('hover', 0);
            }
            mouseOverFeature = feature;
            mouseOverFeature.set('hover', 1);
        }
        mouseOverFeature = feature;
        const [e, n] = evt.coordinate;
        mouseInteraction.onMouseOver(feature, e, n);
    } else {
        clearOverlays();
    }
}, 32);

function clearOverlays() {
    if (mouseOverFeature !== null) {
        if (!mouseOverFeature.get('no_hover_style')) {
            mouseOverFeature.set('hover', 0);
        }
        mouseOverFeature = null;
    }
    setMapCursor(mapWrapper.getInstance(), '');
    MapOverlays.hideTooltip(mapWrapper.getInstance());
}

function loadState() {
    if (store.geofences.items.length) {
        mapWrapper.fitFeatures(geofences.source.getFeatures(), false);
    } else {
        mapWrapper.fitProjectBounds();
    }
}
